import React, {useState, useRef, useEffect} from 'react';
import { VerticalNav } from '../../components/VerticalNavigation/VerticalNav';
import { ModelSpec } from '../../components/ModelSpec/ModelSpec';
import { EditorHeader } from '../../components/EditorHeader/EditorHeader';
import { TabNavigation } from '../../components/TabNavigation/TabNavigation';
import { Canvas } from '../../components/Canvas/Canvas';
import { ConfigPanel } from '../../components/ConfigPanel/ConfigPanel';
import HeaderSection from '../../components/Header/HeaderSection';
import FooterSection from '../../components/Header/FooterSection';
import { useComponentRegistry } from '../../contexts/ComponentRegistryContext';
import { useEditor } from '../../contexts/EditorContext';
import closeMenuIcon from "../../../../../../assests/img/close-menu-icon.svg";
import openMenuIcon from "../../../../../../assests/img/open-menu-icon.svg";
import {useVerticalNav} from "../../hooks/useVerticalNav";
import CustomCSS from "../../components/CustomCSS";
import PopUp from "../../components/PopUp/PopUp";
import CreateUIModel from "../../components/PopUp/CreateUIModel";
import {  Container as ContainerType } from '../../types/canvas.types';
import {useUIModelApi} from "../../contexts/UIModelApiContext";
import {useAutoSave} from "../../hooks/useAutoSave";
import {useNavigationOperations} from "../../hooks/useNavigationOperations";
import { toast } from 'react-toastify';
import ThemeEditor from "../../components/ThemeEditor";
import {TParty} from "../../../../../../types/Context/AppContext";

export type SelectedElementType =
    | { type: 'header'; id: string; }
    | { type: 'footer'; id: string; }
    | { type: 'navigation'; id: string; }
    | { type: 'group'; id: string; }
    | { type: 'page'; id: string; parentId: string; }
    | { type: 'container'; id: string; }
    | { type: 'submodel'; id: string; name: string; }
    | { type: 'field'; id: string; fieldType?: string; parentId?: string;}
    | { type: 'widget'; id: string; widgetType?: string; configuration?: any; };

type ModelEditorProps = {
    id?: string;
    projectId: string;
    selectedParty: TParty;
}

export const ModelEditor = ({ id, projectId, selectedParty}: ModelEditorProps) => {
    const borderStyle = "tw-border-[#D1D1D6]"
    const { setHeaderConfig, setFooterConfig, setNavigationConfig, headerConfig, footerConfig, navigationConfig, setHeaderLogoFile, markPageAsModified,setHeaderResetIconFile, setHeaderCloseIconFile, setFooterPreviousIconFile, setFooterNextIconFile  } = useUIModelApi();
    const [selectedElement, setSelectedElement] = useState<SelectedElementType | undefined>(undefined);
    const [showMenu, setShowMenu] = useState<boolean>(true);
    const [showConfigMenu, setShowConfigMenu] = useState<boolean>(true);
    const [pageContainerRows, setPageContainerRows] = useState<{[pageId: string]: Record<string, number>}>({});
    const { activeTab, setActiveTab } = useVerticalNav();
    const [showCreateUiModel, setShowCreateUiModel] = useState<boolean>(false);
    const {uiModel} = useEditor();

    const [editorConfig, setEditorConfig] = useState<Record<string, any>>({
        'main-header': headerConfig || {
            showHeader: true,
            showTitle: true,
            titleText: 'Title text',
            titleLayout: 'Centre',
            logoUrl: '',
            showResetButton: true,
            resetButtonText: 'Reset',
            resetButtonShowIcon: false,
            resetButtonShowTooltip: false,
            showCloseButton: true,
            closeButtonText: 'Close',
            closeButtonShowIcon: false,
            closeButtonShowTooltip: false,
            resetButtonIconUrl: '',
            closeButtonIconUrl: '',
        },
        'main-footer': footerConfig || {
            showFooter: true,
            showDescription: true,
            descriptionText: 'Footer description or disclaimer text',
            showPreviousButton: true,
            previousButtonText: 'Previous',
            previousButtonShowIcon: false,
            previousButtonShowTooltip: false,
            showNextButton: true,
            nextButtonText: 'Next',
            nextButtonShowIcon: false,
            nextButtonShowTooltip: false,
            previousButtonIconUrl: '',
            nextButtonIconUrl: '',
        },
        'global-navigation': navigationConfig || {
            orientation: 'Horizontal',
            style: 'Block',
            textAlignment: 'Centre',
            showDivider: true,
            showNavigation: true,
            pageSpacing: 10,
            groupSpacing: 10
        }
    });

    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    const { markChanged } = useAutoSave({
        enabled: true,
        debounceMs: 2000,
        pageContainerRows: pageContainerRows,
        onSave: (section) => {
            console.log(`Successfully saved ${section}`);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
        },
        onError: (section, error) => {
            console.error(`Failed to auto-save ${section}:`, error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    });

    const batchOperationInProgress = useRef(false);
    const pendingComponents = useRef<{id: string, type: string}[]>([]);
    const { editorState, containers, selectField, setEditorState, addComponentToContainer, setContainers, addComponentsBatch, groups, setGroups, selectSubmodel, selectWidget, clearAllSelections } = useEditor();

    const prevValuesRef = useRef<{
        header: string;
        navigation: string;
        groups: string;
        containers: string;
        containersByPage?: { [pageId: string]: string };}>({
        header: JSON.stringify(editorConfig['main-header']),
        navigation: JSON.stringify(editorConfig['global-navigation']),
        groups: JSON.stringify(groups),
        containers: JSON.stringify(containers)
    });
    
    useEffect(() => {
        const currentNavigation = JSON.stringify(editorConfig['global-navigation']);
        
        if (currentNavigation !== prevValuesRef.current.navigation) {
            prevValuesRef.current.navigation = currentNavigation;
            setNavigationConfig(editorConfig['global-navigation']);
            markChanged('navigation');
        }
    }, [editorConfig, setNavigationConfig, markChanged]);
    
    useEffect(() => {
        const currentGroups = JSON.stringify(groups);
        if (currentGroups !== prevValuesRef.current.groups) {
            prevValuesRef.current.groups = currentGroups;
            markChanged('navigation');
        }
    }, [groups, markChanged]);

    useEffect(() => {
        if (headerConfig) {
            setEditorConfig(prev => ({
                ...prev,
                'main-header': headerConfig
            }));
        }
    }, [headerConfig]);

    useEffect(() => {
        if (footerConfig) {
            setEditorConfig(prev => ({
                ...prev,
                'main-footer': footerConfig
            }));
        }
    }, [footerConfig]);

    useEffect(() => {
        if (navigationConfig) {
            setEditorConfig(prev => ({
                ...prev,
                'global-navigation': navigationConfig
            }));
        }
    }, [navigationConfig]);

    useEffect(() => {
        if (!editorState.activePageId) return;
        
        const currentPageContainers = containers[editorState.activePageId];
        const currentContainersStr = JSON.stringify(currentPageContainers);
        
        if (!prevValuesRef.current.containersByPage) {
            prevValuesRef.current.containersByPage = {};
        }
        
        const prevContainersStr = prevValuesRef.current.containersByPage[editorState.activePageId];
        
        if (prevContainersStr && currentContainersStr !== prevContainersStr) {
            if (markPageAsModified) {
                markPageAsModified(editorState.activePageId);
            }
            markChanged('canvas');
        }
        
        prevValuesRef.current.containersByPage[editorState.activePageId] = currentContainersStr;
    }, [containers, editorState.activePageId, markChanged, markPageAsModified]);

    const { isLoading: isNavLoading, error: navError,} = useNavigationOperations(projectId);
    
    const { trackComponentUsage, registry, getComponentDefinition } = useComponentRegistry();

    const handleFieldSelect = (fieldId: string, fieldType: string, containerId: string, parentId?: string) => {
        setSelectedElement({ type: 'field', id: fieldId, fieldType: fieldType, parentId: parentId });
        
        setEditorState(prev => ({
            ...prev,
            selectedFieldId: fieldId,
            selectedFieldType: fieldType,
            selectedFieldParentId: parentId || null,
            selectedContainerId: containerId,
        }));
        
        selectField(fieldId, fieldType, parentId, containerId);
    };

    const handleSubmodelSelect = (submodelId: string, containerId?: string) => {
        const submodelDef = registry?.getComponentDefinition(submodelId);
        setSelectedElement({
            type: 'submodel',
            id: submodelId,
            name: submodelDef?.name || 'Unknown Submodel'
        });

        if (selectSubmodel) {
            selectSubmodel(submodelId, containerId);
        }
    };

    const handleWidgetSelect = (widgetId: string, widgetType: string, configuration?: any, containerId?: string) => {
        setSelectedElement({
            type: 'widget',
            id: widgetId,
            widgetType: widgetType,
            configuration: configuration
        });

        // Use selectWidget from context which properly clears field selections
        selectWidget(widgetId, widgetType, configuration, containerId);
    };

    const handleConfigChange = async (newConfig: any) => {
        setEditorConfig(prevConfig => ({
            ...prevConfig,
            [newConfig.id]: newConfig
        }));

        if (selectedElement) {
          try {
            switch (selectedElement.type) {
                case 'header': {
                    setEditorConfig(prevConfig => ({
                        ...prevConfig,
                        [newConfig.id]: newConfig
                    }));
    
                    const { logoFile, resetButtonIconFile, closeButtonIconFile,...headerConfigWithoutFiles } = newConfig;
                    
                    setHeaderConfig(headerConfigWithoutFiles);
                    
                    if (logoFile !== undefined) {
                        setHeaderLogoFile(logoFile);
                    }
                    
                    if (resetButtonIconFile !== undefined) {
                        setHeaderResetIconFile(resetButtonIconFile);
                    }
                    
                    if (closeButtonIconFile !== undefined) {
                        setHeaderCloseIconFile(closeButtonIconFile);
                    }
                    
                    markChanged('header');

                    break;
                }
                case 'footer': {
                    setEditorConfig(prevConfig => ({
                        ...prevConfig,
                        [newConfig.id]: newConfig
                    }));

                    const { previousButtonIconFile, nextButtonIconFile, ...footerConfigWithoutFiles } = newConfig;

                    setFooterConfig(footerConfigWithoutFiles);

                    if (previousButtonIconFile !== undefined) {
                        setFooterPreviousIconFile(previousButtonIconFile);
                    }

                    if (nextButtonIconFile !== undefined) {
                        setFooterNextIconFile(nextButtonIconFile);
                    }

                    markChanged('footer');

                    break;
                }
                case 'navigation':
                    setEditorConfig(prevConfig => ({...prevConfig,[newConfig.id]: newConfig }));

                    setNavigationConfig(newConfig);

                    break;

                case 'group':
                    setGroups(prevGroups => 
                        prevGroups.map(group => 
                            group.id === selectedElement.id 
                                ? {
                                    ...group,
                                    name: newConfig.name,
                                    configuration: {
                                        ...group.configuration,
                                        showTitle: newConfig.showTitle
                                    }
                                }
                                : group
                        )
                    );
                    break;

                case 'page':
                        setGroups(prevGroups => 
                            prevGroups.map(group => {
                                if (group.id === selectedElement.parentId) {
                                    return {
                                        ...group,
                                        pages: group.pages.map(page => 
                                            page.id === selectedElement.id
                                                ? {
                                                    ...page,
                                                    name: newConfig.title,
                                                    configuration: {
                                                        ...page.configuration,
                                                        autoAdvance: newConfig.autoAdvance
                                                    }
                                                }
                                            : page
                                        )
                                    };
                                }
                                return group;
                            })
                        );
                        
                        break;
                case 'field': {
                    const fieldId = selectedElement.id;
                    // Use getComponentDefinition from context which handles null registry
                    const fieldDef = getComponentDefinition(fieldId);
                    const isCategory = fieldDef?.type === 'category' || selectedElement.fieldType === 'category';
                    
                    const updateFieldInContainers = (containers: ContainerType[]): ContainerType[] => {
                        return containers.map(container => {
                            const updatedItems = container.content.items?.map(item => {
                                if ((item.type === 'field' || item.type === 'category') && item.id === fieldId) {
                                    // For category fields, preserve all configuration properties
                                    if (isCategory) {
                                        const { id, categoryId, isEmpty, ...restConfig } = newConfig;
                                        return {
                                            ...item,
                                            configuration: {
                                                ...item.configuration,
                                                ...restConfig,
                                                // Preserve category-specific IDs
                                                id: id || item.configuration?.id,
                                                categoryId: categoryId || item.configuration?.categoryId,
                                                isEmpty: isEmpty !== undefined ? isEmpty : item.configuration?.isEmpty
                                            },
                                            dataConfiguration: {
                                                ...item.dataConfiguration,
                                                id: id || item.dataConfiguration?.id,
                                                categoryId: categoryId || item.dataConfiguration?.categoryId,
                                                isEmpty: isEmpty !== undefined ? isEmpty : item.dataConfiguration?.isEmpty
                                            }
                                        };
                                    } else {
                                        // For regular fields - use spread to include all properties
                                        const { id, ...configWithoutId } = newConfig;
                                        return {
                                            ...item,
                                            configuration: {
                                                ...item.configuration,
                                                ...configWithoutId
                                            }
                                        };
                                    }
                                }
                                return item;
                            });
                            
                            let updatedNestedContainers = container.content.nestedContainers;
                            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                                updatedNestedContainers = updateFieldInContainers(container.content.nestedContainers);
                            }
                            
                            return {
                                ...container,
                                content: {
                                    ...container.content,
                                    items: updatedItems || [],
                                    nestedContainers: updatedNestedContainers || []
                                }
                            };
                        });
                    };
                    
                    const activePageId = editorState.activePageId;
                    if (activePageId) {
                        const updatedContainers = updateFieldInContainers(containers[activePageId]);
                        setContainers(prev => ({
                            ...prev,
                            [activePageId]: updatedContainers
                        }));

                        // Note: markChanged('canvas') is handled by useEffect that watches containers
                        // No need to call it explicitly here to avoid duplicate API calls

                        if (markPageAsModified) {
                            markPageAsModified(activePageId);
                        }
                    }

                    setSaveStatus('saving');

                    break;
                }
                            
                case 'widget': {
                    const widgetId = selectedElement.id;

                    // Handle all widget types (yourBuild, button, etc.)
                    const updateWidgetInContainers = (containers: ContainerType[]): ContainerType[] => {
                        return containers.map(container => {
                            const updatedItems = container.content.items?.map(item => {
                                if (item.type === 'widget' && item.id === widgetId) {
                                    return {
                                        ...item,
                                        configuration: newConfig
                                    };
                                }
                                return item;
                            });

                            let updatedNestedContainers = container.content.nestedContainers;
                            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                                updatedNestedContainers = updateWidgetInContainers(container.content.nestedContainers);
                            }

                            return {
                                ...container,
                                content: {
                                    ...container.content,
                                    items: updatedItems || [],
                                    nestedContainers: updatedNestedContainers || []
                                }
                            };
                        });
                    };

                    const activePageId = editorState.activePageId;
                    if (activePageId) {
                        const updatedContainers = updateWidgetInContainers(containers[activePageId]);
                        setContainers(prev => ({
                            ...prev,
                            [activePageId]: updatedContainers
                        }));

                        // Note: markChanged('canvas') is handled by useEffect that watches containers
                        // No need to call it explicitly here to avoid duplicate API calls

                        if (markPageAsModified) {
                            markPageAsModified(activePageId);
                        }
                    }

                    setSaveStatus('saving');
                    break;
                }
            }
            } catch (err) {
                console.error("Error in configuration update:", err);
            }
        }
    };

    useEffect(() => {
        if (navError) {
            toast.error('Failed to perform navigation operation. Please try again later.' + navError );
        }
    }, [navError]);
    
    const handleSelectNavigation = () => {
        clearAllSelections();
        setSelectedElement({
            type: 'navigation',
            id: 'global-navigation'
        });
    };

    const handleSelectGroup = (groupId: string) => {
        clearAllSelections();
        setSelectedElement({
            type: 'group',
            id: groupId
        });
    };
    
    const handleSelectPage = (pageId: string, groupId: string) => {
        clearAllSelections();
        setSelectedElement({
            type: 'page',
            id: pageId,
            parentId: groupId
        });
    };

    const handleSelectHeader = () => {
        clearAllSelections();
        setSelectedElement({
            type: 'header',
            id: 'main-header'
        });

        setEditorState(prev => ({
            ...prev,
            selectedContainerId: null
        }));
    };

    const handleSelectFooter = () => {
        clearAllSelections();
        setSelectedElement({
            type: 'footer',
            id: 'main-footer'
        });

        setEditorState(prev => ({
            ...prev,
            selectedContainerId: null
        }));
    };

    const handleSelectContainer = (containerId: string) => {
        if (!containerId) {
            setSelectedElement(undefined);
            return;
        }

        clearAllSelections();
        setSelectedElement({
            type: 'container',
            id: containerId
        });

        setEditorState(prev => ({
            ...prev,
            selectedContainerId: containerId
        }));
    };

    const processPendingComponents = () => {
        if (pendingComponents.current.length === 0 || !editorState.selectedContainerId) {
            batchOperationInProgress.current = false;
            return;
        }
        
        batchOperationInProgress.current = true;
        
        const component = pendingComponents.current.shift();
        
        if (component && editorState.activeGroupId && editorState.activePageId) {
            trackComponentUsage(component.id, {
                containerId: editorState.selectedContainerId,
                groupId: editorState.activeGroupId,
                pageId: editorState.activePageId
            });
            
            addComponentToContainer(editorState.selectedContainerId, component.id, component.type);
            
            if (pendingComponents.current.length > 0) {
                requestAnimationFrame(processPendingComponents);
            } else {
                batchOperationInProgress.current = false;
            }
        } else {
            batchOperationInProgress.current = false;
        }
    };

    const handleAddComponent = (componentId: string, componentType: string, childComponents?: {id: string, type: string}[]) => {
        if (!editorState.selectedContainerId || !editorState.activeGroupId || !editorState.activePageId) {
            console.warn('Cannot add component: No container, group, or tab selected');
            return;
        }
        
        trackComponentUsage(componentId, {
            containerId: editorState.selectedContainerId,
            groupId: editorState.activeGroupId,
            pageId: editorState.activePageId
        });
        
        addComponentToContainer(editorState.selectedContainerId, componentId, componentType);

        if (childComponents && childComponents.length > 0) {
            pendingComponents.current = childComponents;
            
            if (!batchOperationInProgress.current) {
                requestAnimationFrame(processPendingComponents);
            }
        }
    };

    const handleAddComponentsBatch = ( submodelId: string,  submodelType: string,  childComponents: {id: string, type: string}[]) => {
        if (!editorState.selectedContainerId || !editorState.activeGroupId || !editorState.activePageId) {
            console.warn('Cannot add components: No container, group, or tab selected');
            return;
        }
        
        trackComponentUsage(submodelId, {
            containerId: editorState.selectedContainerId,
            groupId: editorState.activeGroupId,
            pageId: editorState.activePageId
        });
        
        const componentsToProcess = [
            { id: submodelId, type: submodelType },
            ...childComponents
        ];
        
        addComponentsBatch(editorState.selectedContainerId, componentsToProcess);
        
        childComponents.forEach(child => {
            trackComponentUsage(child.id, {
                containerId: editorState.selectedContainerId!,
                groupId: editorState.activeGroupId!,
                pageId: editorState.activePageId!
            });
        });
    };

    const handleContainerRowsChange = (pageId: string, containerRows: Record<string, number>) => {
        setPageContainerRows(prev => ({
            ...prev,
            [pageId]: containerRows
        }));
    };

    const getSelectedElementConfig = () => {
        if (!selectedElement) return undefined;
        
        if (selectedElement.type === 'group') {
            const group = groups.find(g => g.id === selectedElement.id);
            if (group) {
                return {
                    id: group.id,
                    name: group.name,
                    showTitle: group.configuration.showTitle,
                };
            }
        }
        
        if (selectedElement.type === 'page' && selectedElement.parentId) {
            const group = groups.find(g => g.id === selectedElement.parentId);
            if (group) {
                const page = group.pages.find(t => t.id === selectedElement.id);
                if (page) {
                    return {
                        id: page.id,
                        title: page.name,
                        autoAdvance: page.configuration.autoAdvance
                    };
                }
            }
        }
        
        return editorConfig[selectedElement.id];
    };

    const renderLoadingOverlay = () => {
        if (!isNavLoading) return null;
        
        return (
            <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-30 tw-flex tw-items-center tw-justify-center tw-z-50">
                <div className="tw-bg-white tw-p-4 tw-rounded-md tw-shadow-lg">
                    <div className="tw-flex tw-items-center">
                        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-t-2 tw-border-b-2 tw-border-blue-500 tw-mr-3"></div>
                        <span>Saving changes...</span>
                    </div>
                </div>
            </div>
        );
    };

    const getTabContent = () => {
        switch(activeTab) {
            case 'layout':
                return <div className="tw-flex tw-flex-1 tw-overflow-hidden">
                    <div className={`${showMenu ? 'tw-w-64' : 'tw-w-0'} tw-border-r ${borderStyle} tw-flex-shrink-0 tw-overflow-auto tw-mt-1.5`}>
                    <ModelSpec
                        selectedContainerId={editorState.selectedContainerId || undefined}
                        onAddComponent={handleAddComponent}
                        onAddComponentsBatch={handleAddComponentsBatch}
                    />
                </div>

                <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1 tw-overflow-hidden">
                    <div onClick={handleSelectHeader} className="tw-flex-shrink-0 tw-p-1">
                        <HeaderSection
                            config={editorConfig['main-header']}
                            isSelected={selectedElement?.type === 'header'}
                        />
                    </div>

                    <div className="tw-flex-shrink-0 tw-p-1">
                        <TabNavigation
                            onNavigationSelect={handleSelectNavigation}
                            onGroupSelect={handleSelectGroup}
                            onPageSelect={handleSelectPage}
                            selectedElement={selectedElement}
                            navigationConfig={editorConfig['global-navigation']}
                        />
                    </div>

                    <div className="tw-flex-1 tw-overflow-auto tw-min-h-0">
                        <Canvas
                            onContainerSelect={handleSelectContainer}
                            onFieldSelect={handleFieldSelect}
                            onSubmodelSelect={handleSubmodelSelect}
                            onWidgetSelect={handleWidgetSelect}
                            configureId={projectId}
                            onContainerRowsChange={handleContainerRowsChange}
                        />
                    </div>

                    <div onClick={handleSelectFooter} className="tw-flex-shrink-0 tw-p-1">
                        <FooterSection
                            config={editorConfig['main-footer']}
                            isSelected={selectedElement?.type === 'footer'}
                        />
                    </div>
                </div>

                <div className={`${showConfigMenu ? 'tw-w-[290px]' : 'tw-w-0'} tw-border-l ${borderStyle} tw-flex-shrink-0 tw-overflow-auto tw-mt-1.5`}>
                    <ConfigPanel
                        projectId={projectId}
                        selectedElement={selectedElement}
                        config={getSelectedElementConfig()}
                        onConfigChange={handleConfigChange}
                    />
                </div>
            </div>;
            case 'theme':
                return <ThemeEditor projectId={projectId} />;
            case 'css':
                return <CustomCSS projectId={projectId}/>;
            default:
                return null;
        }
    };

    const launchPreview = () => {
        let selectedPartyName = (selectedParty!.name.replace(/ /g, "_")).toLowerCase();
        const rootPreviewURL: string = process.env.REACT_APP_ROOT_PREVIEW_URL ?? '';
        const previewUrl: string = `https://${selectedPartyName}.${rootPreviewURL}/preview?modelId=${projectId}&uiModelId=${uiModel?.id}`
        window.open(previewUrl, '_blank')!.focus();
    }

    return (
        <>
        <div className="tw-flex tw-flex-col tw-h-screen tw-overflow-hidden">
            {renderLoadingOverlay()}
            
            <div className={`tw-h-9 tw-border-b ${borderStyle}`}>
                <EditorHeader uiModel={uiModel} setShowCreateUiModel={setShowCreateUiModel} onPreviewPressed={launchPreview}/>
            </div>

            <div className="tw-relative tw-flex tw-flex-1 tw-overflow-hidden">
                {activeTab === 'layout' ? <>
                    <button
                        id="sidebar-toggle-btn"
                        className={`tw-absolute ${showMenu ? "tw-left-[310px]" : "tw-left-[54px]"} tw-top-1 tw-transition-width tw-duration-100 tw-bg-white tw-z-20 tw-p-0 tw-m-0 tw-h-min`}
                        onClick={() => setShowMenu((prevShowMenu) => !prevShowMenu)}
                    >
                        <img
                            id="sidebar-toggle-icon"
                            className={`tw-w-6 tw-aspect-square`}
                            src={showMenu ? closeMenuIcon : openMenuIcon}
                            alt="menuIcon"/>
                    </button>

                </> : null}

                {activeTab === 'layout' ? <>
                    <button
                        id="config-panel-toggle-btn"
                        className={`tw-absolute ${showConfigMenu ? "tw-right-[278px]" : "tw-right-0"} tw-top-1 tw-transition-width tw-duration-100 tw-bg-white tw-z-20 tw-p-0 tw-m-0 tw-h-min`}
                        onClick={() => setShowConfigMenu((prev) => !prev)}
                    >
                        <img
                            id="config-panel-toggle-icon"
                            className={`tw-w-6 tw-aspect-square`}
                            src={showConfigMenu ? openMenuIcon : closeMenuIcon}
                            alt="configMenuIcon"/>
                    </button>

                </> : null}

            <div className="tw-flex tw-flex-1 tw-overflow-hidden">
                <div className={`tw-w-16 tw-border-r ${borderStyle} tw-flex-shrink-0`}>
                    <VerticalNav />
                </div>
                {getTabContent()}
            </div>
            {saveStatus !== 'idle' && (
                <div className="tw-fixed tw-bottom-4 tw-right-4 tw-z-50">
                    <div className={`tw-px-4 tw-py-2 tw-rounded-md tw-shadow-lg tw-flex tw-items-center tw-space-x-2
                        ${saveStatus === 'saving' ? 'tw-bg-blue-100 tw-text-blue-700' : ''}
                        ${saveStatus === 'saved' ? 'tw-bg-green-100 tw-text-green-700' : ''}
                        ${saveStatus === 'error' ? 'tw-bg-red-100 tw-text-red-700' : ''}
                    `}>
                        {saveStatus === 'saving' && (
                            <>
                                <svg className="tw-animate-spin tw-h-4 tw-w-4" viewBox="0 0 24 24">
                                    <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <span className="tw-text-sm">Saving...</span>
                            </>
                        )}
                        
                        {saveStatus === 'saved' && (
                            <>
                                <svg className="tw-h-4 tw-w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="tw-text-sm">All changes saved</span>
                            </>
                        )}
                        
                        {saveStatus === 'error' && (
                            <>
                                <svg className="tw-h-4 tw-w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="tw-text-sm">Failed to save</span>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
            </div>
            {showCreateUiModel && (
                <PopUp
                    title="Update UI Model"
                    content={
                        <CreateUIModel
                            projectId={projectId}
                            uiModel={uiModel}
                            onSuccess={() => setShowCreateUiModel(false)}
                            onClose={() => setShowCreateUiModel(false)}
                        />
                    }
                />
            )}
            </>
    );
};

export default ModelEditor;
